import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, X } from "lucide-react";

import axios from "../../../services/axiosInstance";
import { fetchMasterWarehouses } from "../raking/warehouseSlice";

const PAGE_SIZE = 30;

const fmt = (n) =>
    n === null || n === undefined || Number.isNaN(Number(n))
        ? "-"
        : Number(n).toLocaleString("id-ID");

/* ============ Kartu hasil ============ */

function LocationCard({ item }) {
    return (
        <article className="bg-white rounded-2xl border border-line p-5 flex flex-col">

            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-muted tabular-nums">
                        {item.sku}
                    </div>
                    <h3 className="text-[17px] font-bold text-body leading-snug mt-0.5 break-words">
                        {item.item_name || "-"}
                    </h3>
                </div>

                <div className="text-right shrink-0">
                    <div className="text-sm text-muted">Qty</div>
                    <div className="text-[28px] leading-none font-bold text-emerald-600 tabular-nums mt-1">
                        {fmt(item.qty)}
                    </div>
                </div>
            </div>

            <div className="text-sm text-muted mt-3">
                Batch <span className="text-body tabular-nums">{item.batch_no || "-"}</span>
            </div>

            <div className="border-t border-line my-4" />

            <dl className="grid grid-cols-2 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto_auto] gap-x-5 gap-y-3 text-sm">
                {[
                    ["Warehouse", item.warehouse],
                    ["Rack", item.racks_name || item.rack_code],
                    ["Level", item.level_code],
                    ["Pallet", item.kode_palet]
                ].map(([label, value]) => (
                    <div key={label} className="min-w-0">
                        <dt className="text-muted">{label}</dt>
                        <dd className="font-semibold text-body truncate tabular-nums mt-0.5" title={value || ""}>
                            {value || "-"}
                        </dd>
                    </div>
                ))}
            </dl>
        </article>
    );
}

/* ============ Halaman ============ */

export default function InventoryPage() {
    const dispatch = useDispatch();
    const warehouses = useSelector((state) => state.masterWarehouses?.data) || [];

    const [warehouseId, setWarehouseId] = useState("");
    const [keyword, setKeyword] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [visible, setVisible] = useState(PAGE_SIZE);

    const requestId = useRef(0);

    useEffect(() => {
        if (!warehouses.length) dispatch(fetchMasterWarehouses());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const term = keyword.trim();
    // cari kalau ada kata kunci (min. 2 huruf) atau warehouse dipilih
    const canSearch = term.length >= 2 || Boolean(warehouseId);

    useEffect(() => {
        if (!canSearch) {
            setItems([]);
            setError(null);
            setLoading(false);
            return;
        }

        const id = ++requestId.current;
        setLoading(true);
        setError(null);

        const timer = setTimeout(async () => {
            try {
                const res = await axios.get("/warehouse/location", {
                    params: {
                        keyword: term || undefined,
                        warehouse_id: warehouseId || undefined
                    }
                });
                if (id !== requestId.current) return; // hasil request lama, abaikan
                setItems(Array.isArray(res.data?.item) ? res.data.item : []);
                setVisible(PAGE_SIZE);
            } catch (err) {
                if (id !== requestId.current) return;
                setItems([]);
                setError(
                    err.response?.data?.message ||
                    "Data lokasi tidak bisa diambil. Periksa koneksi ke server lalu coba lagi."
                );
            } finally {
                if (id === requestId.current) setLoading(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [term, warehouseId, canSearch]);

    const totalQty = useMemo(
        () => items.reduce((a, it) => a + Number(it.qty || 0), 0),
        [items]
    );

    const shown = items.slice(0, visible);

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            {/* ===== JUDUL + FILTER ===== */}
            <div>
                <h2 className="text-2xl font-semibold text-body tracking-tight">
                    Warehouse overview
                </h2>
                <p className="text-sm text-muted mt-1">
                    Cari di rak mana sebuah item disimpan
                </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-end gap-3">

                <label className="flex flex-col gap-1 md:w-72">
                    <span className="text-sm font-semibold text-body">Filter warehouse</span>
                    <select
                        value={warehouseId}
                        onChange={(e) => setWarehouseId(e.target.value)}
                        className="h-11 bg-white border border-line rounded-lg px-3 text-[15px] text-body focus:border-ink focus:ring-0"
                    >
                        <option value="">Semua warehouse</option>
                        {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                                {w.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="flex-1 relative">
                    <span className="sr-only">Cari</span>
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    <input
                        type="text"
                        inputMode="search"
                        enterKeyHint="search"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        placeholder="Cari rack / item code / item name / batch"
                        className="h-11 w-full bg-white border border-line rounded-lg pl-11 pr-10 text-[15px] text-body placeholder:text-muted/70 focus:border-ink focus:ring-0"
                        autoFocus
                    />
                    {keyword && (
                        <button
                            type="button"
                            onClick={() => setKeyword("")}
                            aria-label="Hapus pencarian"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted hover:text-body hover:bg-surface"
                        >
                            <X size={16} />
                        </button>
                    )}
                </label>
            </div>

            {/* ===== RINGKASAN ===== */}
            {canSearch && !error && (
                <div className="text-sm text-muted min-h-[20px]" aria-live="polite">
                    {loading
                        ? "Mencari…"
                        : items.length > 0
                            ? <>
                                <span className="font-semibold text-body">{fmt(items.length)}</span> lokasi ditemukan,
                                total qty <span className="font-semibold text-body">{fmt(totalQty)}</span>
                            </>
                            : null}
                </div>
            )}

            {/* ===== HASIL ===== */}
            {!canSearch ? (
                <div className="bg-white rounded-2xl border border-dashed border-line px-6 py-16 text-center">
                    <div className="text-body font-semibold">Mulai cari lokasi stok</div>
                    <p className="text-sm text-muted mt-1">
                        Ketik minimal 2 huruf kode item, nama item, rak, atau batch. Atau pilih warehouse untuk melihat semua isinya.
                    </p>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-2xl px-6 py-5 text-sm text-red-700">
                    {error}
                </div>
            ) : !loading && items.length === 0 ? (
                <div className="bg-white rounded-2xl border border-line px-6 py-16 text-center">
                    <div className="text-body font-semibold">Tidak ada stok yang cocok</div>
                    <p className="text-sm text-muted mt-1">
                        {term
                            ? `Tidak ada item di rak untuk "${term}". Periksa ejaan atau coba kata kunci lain.`
                            : "Warehouse ini belum punya stok di rak."}
                    </p>
                </div>
            ) : (
                <>
                    <div className={`grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
                        {shown.map((it, i) => (
                            <LocationCard
                                key={`${it.rack_code}-${it.level_code}-${it.kode_palet}-${it.sku}-${it.batch_no}-${i}`}
                                item={it}
                            />
                        ))}
                    </div>

                    {visible < items.length && (
                        <div className="flex justify-center pt-2">
                            <button
                                type="button"
                                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                                className="h-10 px-5 text-sm font-medium text-ink bg-white border border-line rounded-lg hover:border-ink"
                            >
                                Tampilkan {fmt(Math.min(PAGE_SIZE, items.length - visible))} lagi
                                <span className="text-muted font-normal"> dari sisa {fmt(items.length - visible)}</span>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
